import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CreditCard, Settings, RefreshCw, DollarSign, KeyRound, Coins, Bell,
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Package, GripVertical,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

const API = "/api";

const EMPTY_PLAN = {
  name: "",
  monthly_price: "",
  classes_per_month: "",
  classes_per_week: "",
  description: "",
  active: true,
};

export default function AdminPayments() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansOpen, setPlansOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);   // plan object or null
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [savingPlan, setSavingPlan] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [txnRes, priceRes, plansRes] = await Promise.all([
        axios.get(`${API}/payments/admin/transactions`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/admin/pricing`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/admin/plans`, { headers: getAuthHeader() }),
      ]);
      setTransactions(txnRes.data.items || []);
      setPricing(priceRes.data);
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      console.error("[AdminPayments] load failed", err);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNewPlan = () => {
    setEditingPlan(null);
    setPlanForm(EMPTY_PLAN);
  };
  const openEditPlan = (p) => {
    setEditingPlan(p);
    setPlanForm({
      name: p.name,
      monthly_price: p.monthly_price,
      classes_per_month: p.classes_per_month,
      classes_per_week: p.classes_per_week || 0,
      description: p.description || "",
      active: p.active !== false,
    });
  };

  const savePlan = async () => {
    // Validate
    const price = parseFloat(planForm.monthly_price);
    const cpm = parseInt(planForm.classes_per_month, 10);
    const cpw = parseInt(planForm.classes_per_week || 0, 10);
    if (!planForm.name.trim()) return toast.error("Plan name is required");
    if (!(price > 0)) return toast.error("Monthly price must be greater than 0");
    if (!(cpm > 0)) return toast.error("Classes per month must be greater than 0");

    setSavingPlan(true);
    try {
      const payload = {
        name: planForm.name.trim(),
        monthly_price: price,
        classes_per_month: cpm,
        classes_per_week: cpw,
        description: planForm.description || "",
        active: !!planForm.active,
      };
      if (editingPlan) {
        await axios.put(`${API}/payments/admin/plans/${editingPlan.id}`, payload, { headers: getAuthHeader() });
        toast.success(`"${payload.name}" updated`);
      } else {
        await axios.post(`${API}/payments/admin/plans`, payload, { headers: getAuthHeader() });
        toast.success(`"${payload.name}" created`);
      }
      setEditingPlan(null);
      setPlanForm(EMPTY_PLAN);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const toggleActive = async (plan, newActive) => {
    try {
      await axios.put(`${API}/payments/admin/plans/${plan.id}`, { active: newActive }, { headers: getAuthHeader() });
      toast.success(`"${plan.name}" ${newActive ? "activated" : "deactivated"}`);
      setPlans((prev) => prev.map((p) => p.id === plan.id ? { ...p, active: newActive } : p));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update plan");
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"?\n\nStudents already on this plan keep their credits — only the plan itself is removed. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/payments/admin/plans/${plan.id}`, { headers: getAuthHeader() });
      toast.success(`"${plan.name}" deleted`);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete plan");
    }
  };

  const movePlan = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= plans.length) return;
    const reordered = [...plans];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // Optimistically update UI
    setPlans(reordered.map((p, i) => ({ ...p, sort_order: i + 1 })));
    try {
      await axios.put(
        `${API}/payments/admin/plans/reorder`,
        { plan_ids: reordered.map((p) => p.id) },
        { headers: getAuthHeader() }
      );
    } catch (err) {
      toast.error("Failed to reorder — reverting");
      fetchData();
    }
  };

  const paidTxns = transactions.filter(t => t.payment_status === "paid");
  const totalRevenue = paidTxns.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalClasses = paidTxns.reduce((sum, t) => sum + (t.classes_purchased || 0), 0);
  const activePlansCount = plans.filter((p) => p.active).length;

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-teal-100">
            <CreditCard className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payment Dashboard</h1>
            <p className="text-sm text-slate-500">{paidTxns.length} successful payments</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate("/admin/reminders")} data-testid="reminders-link"
            className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <Bell className="w-4 h-4 mr-1.5" /> Reminder Emails
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/paymongo-keys")} data-testid="paymongo-keys-link"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <KeyRound className="w-4 h-4 mr-1.5" /> PayMongo Keys
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/fx-rate")} data-testid="fx-rate-link"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Coins className="w-4 h-4 mr-1.5" /> FX Rate
          </Button>
          <Button variant="outline" onClick={() => setPlansOpen(true)} data-testid="pricing-settings-btn"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
            <Package className="w-4 h-4 mr-1.5" /> Subscription Plans
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-teal-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg"><DollarSign className="w-5 h-5 text-teal-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Total Revenue</p>
                <p className="text-xl font-bold text-slate-800">{totalRevenue.toLocaleString()} {pricing?.currency_symbol || "TL"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><CreditCard className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Classes Sold</p>
                <p className="text-xl font-bold text-slate-800">{totalClasses}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg"><Package className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Active Plans</p>
                <p className="text-xl font-bold text-slate-800">{activePlansCount} <span className="text-sm font-normal text-slate-400">/ {plans.length}</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">All Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No transactions yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Student</TableHead>
                  <TableHead className="font-semibold">Username</TableHead>
                  <TableHead className="font-semibold">Plan / Package</TableHead>
                  <TableHead className="font-semibold text-center">Classes</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map(txn => (
                  <TableRow key={txn.id}>
                    <TableCell className="font-medium">{txn.student_name}</TableCell>
                    <TableCell className="text-slate-500">{txn.username || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {txn.plan_name || txn.package_id || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700">
                        {txn.classes_purchased}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{txn.amount} {txn.currency_symbol || "TL"}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        txn.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" :
                        txn.payment_status === "pending" ? "bg-amber-50 text-amber-700" :
                        "bg-red-50 text-red-700"
                      }`}>
                        {txn.payment_status === "paid" ? "Paid" : txn.payment_status === "pending" ? "Pending" : txn.payment_status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {txn.created_at ? new Date(txn.created_at).toLocaleString() : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subscription Plans Manager Dialog */}
      <Dialog open={plansOpen} onOpenChange={setPlansOpen}>
        <DialogContent className="sm:max-w-3xl" data-testid="plans-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" /> Subscription Plans
            </DialogTitle>
            <DialogDescription>
              Create, edit, reorder, activate/deactivate and delete the monthly plans that appear on the student checkout page. Currency: Turkish Lira (TL).
            </DialogDescription>
          </DialogHeader>

          {/* Plans list */}
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="plans-list">
            {plans.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-4 text-center">No plans yet — add your first plan below.</p>
            ) : plans.map((p, i) => (
              <div
                key={p.id}
                className={cn(
                  "flex items-center gap-2 border rounded-md p-3",
                  p.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
                )}
                data-testid={`plan-row-${p.id}`}
              >
                <div className="flex flex-col gap-0.5">
                  <button type="button" onClick={() => movePlan(i, -1)} disabled={i === 0}
                    className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`plan-move-up-${p.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => movePlan(i, 1)} disabled={i === plans.length - 1}
                    className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`plan-move-down-${p.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 truncate">{p.name}</span>
                    {!p.active && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-emerald-700">{p.monthly_price.toLocaleString()} {p.currency_symbol || "TL"}</span>
                    <span>·</span>
                    <span>{p.classes_per_month} classes/mo</span>
                    {p.classes_per_week > 0 && <><span>·</span><span>{p.classes_per_week}/week</span></>}
                    {p.description && <><span>·</span><span className="italic truncate">{p.description}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Switch
                    checked={p.active}
                    onCheckedChange={(val) => toggleActive(p, val)}
                    data-testid={`plan-toggle-${p.id}`}
                    aria-label={`${p.active ? "Deactivate" : "Activate"} ${p.name}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => openEditPlan(p)} data-testid={`plan-edit-${p.id}`}>
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deletePlan(p)} data-testid={`plan-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Create / Edit form */}
          <div className="border-t pt-4 space-y-3" data-testid="plan-form">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                {editingPlan ? <><Pencil className="w-4 h-4" /> Edit "{editingPlan.name}"</> : <><Plus className="w-4 h-4" /> New Plan</>}
              </p>
              {editingPlan && (
                <button type="button" onClick={openNewPlan} className="text-xs text-indigo-600 hover:underline" data-testid="plan-form-new">
                  + Add different plan instead
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Plan Name *</Label>
                <Input value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  placeholder="e.g. 5 Classes per Week"
                  data-testid="plan-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Monthly Price (TL) *</Label>
                <Input type="number" value={planForm.monthly_price}
                  onChange={(e) => setPlanForm({ ...planForm, monthly_price: e.target.value })}
                  placeholder="e.g. 1100"
                  data-testid="plan-form-price" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Classes per Month *</Label>
                <Input type="number" value={planForm.classes_per_month}
                  onChange={(e) => setPlanForm({ ...planForm, classes_per_month: e.target.value })}
                  placeholder="e.g. 20"
                  data-testid="plan-form-cpm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Classes per Week <span className="text-slate-400">(display only)</span></Label>
                <Input type="number" value={planForm.classes_per_week}
                  onChange={(e) => setPlanForm({ ...planForm, classes_per_week: e.target.value })}
                  placeholder="e.g. 5"
                  data-testid="plan-form-cpw" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Description <span className="text-slate-400">(optional, e.g. "Most popular")</span></Label>
                <Textarea rows={2} value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  data-testid="plan-form-description" />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Switch checked={planForm.active}
                  onCheckedChange={(v) => setPlanForm({ ...planForm, active: v })}
                  data-testid="plan-form-active" />
                <Label className="text-sm cursor-pointer" onClick={() => setPlanForm({ ...planForm, active: !planForm.active })}>
                  Active (shown to students at checkout)
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPlansOpen(false)} data-testid="plans-dialog-close">Close</Button>
            <Button onClick={savePlan} disabled={savingPlan}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="plan-save-btn">
              {savingPlan ? "Saving..." : editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
